/**
 * Modal pour imprimer des étiquettes produit
 * Supporte l'impression via Hardware Bridge local (POS) ou API backend
 *
 * Phase 4 améliorations :
 * - Dropdown lots avec infos (fournisseur, DLC, prix)
 * - Affichage du prix résolu
 * - Auto-preview avec debounce
 * - Messages inline au lieu de alert()
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { labelTemplatesApi } from '../../api/label-templates.api';
import { LabelTemplate, LabelTemplateType } from '../../types/label-templates.types';
import { api } from '../../services/api';
import { posApi } from '../../api/pos.api';
import { getHardwareBridgeStatus, hardwareBridgeZebra, zebraAbsenteIci } from '../../services/printers/HardwareBridgeAdapter';

interface Props {
  onClose: () => void;
  preselectedProductId?: string;
  preselectedLotId?: string;
}

interface Product {
  id: string;
  name: string;
  code?: string;
  barcode?: string;
  suggested_price_cents?: number;
  prices?: { amount_cents: number }[];
}

interface Lot {
  id: string;
  lot_number: string;
  supplier_lot_number?: string;
  reception_date: string;
  expiry_date?: string;
  stock_backroom: number;
  stock_display: number;
  unit?: string;
  purchase_price_cents?: number;
  unit_price_ttc_cents?: number;
  suggested_price_cents?: number;
  supplier_name?: string;
}

type StatusMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
} | null;

export function PrintShelfLabelsModal({ onClose, preselectedProductId, preselectedLotId }: Props) {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Lots
  const [lots, setLots] = useState<Lot[]>([]);
  const [selectedLotId, setSelectedLotId] = useState(preselectedLotId || '');
  const [lotsLoading, setLotsLoading] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<StatusMessage>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const previewTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    loadTemplates();
    if (preselectedProductId) {
      loadPreselectedProduct(preselectedProductId);
    }
  }, []);

  // Charger les lots quand un produit est sélectionné
  useEffect(() => {
    if (selectedProduct) {
      loadLots(selectedProduct.id);
    } else {
      setLots([]);
      setSelectedLotId('');
    }
  }, [selectedProduct?.id]);

  // Auto-preview avec debounce quand produit/lot/template changent
  useEffect(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    if (selectedProduct?.id && selectedTemplateId) {
      previewTimeoutRef.current = setTimeout(() => {
        loadPreview();
      }, 600);
    } else {
      setPreviewUrl(null);
    }

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [selectedProduct?.id, selectedLotId, selectedTemplateId]);

  const loadPreselectedProduct = async (id: string) => {
    try {
      const product = await api.get<Product>(`/products/${id}`);
      const productData = (product as any).data || product;
      if (productData) {
        setSelectedProduct(productData);
        setProductSearch(productData.name);
      }
    } catch (error) {
      console.error('Erreur chargement produit pré-sélectionné:', error);
    }
  };

  const loadLots = async (productId: string) => {
    try {
      setLotsLoading(true);
      const data = await posApi.getProductLots(productId);
      setLots(data || []);

      // Auto-sélection : lot pré-sélectionné > premier lot actif
      if (preselectedLotId && data?.some((l: Lot) => l.id === preselectedLotId)) {
        setSelectedLotId(preselectedLotId);
      } else if (data && data.length > 0) {
        setSelectedLotId(data[0].id);
      } else {
        setSelectedLotId('');
      }
    } catch (error) {
      console.error('Erreur chargement lots:', error);
      setLots([]);
    } finally {
      setLotsLoading(false);
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (productSearch.length >= 2 && !selectedProduct) {
      searchTimeoutRef.current = setTimeout(() => {
        searchProducts(productSearch);
      }, 300);
    } else {
      setProductResults([]);
      setShowProductDropdown(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [productSearch, selectedProduct]);

  const loadTemplates = async () => {
    try {
      const data = await labelTemplatesApi.findAll();
      setTemplates(data);

      const defaultShelf = data.find(t => t.type === LabelTemplateType.SHELF && t.is_default);
      if (defaultShelf) {
        setSelectedTemplateId(defaultShelf.id);
      } else if (data.length > 0) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const searchProducts = async (query: string) => {
    try {
      const products = await api.get<any>('/products', {
        params: { search: query, limit: 10 },
      });
      setProductResults(products.data || products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.error('Erreur recherche produits:', error);
      setProductResults([]);
    }
  };

  const selectProduct = (product: Product) => {
    setSelectedProduct(product);
    setProductSearch(product.name);
    setShowProductDropdown(false);
    setPreviewUrl(null);
    setStatusMessage(null);
  };

  const clearProduct = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setLots([]);
    setSelectedLotId('');
    setPreviewUrl(null);
    setStatusMessage(null);
  };

  // Résolution du prix — MÊME cascade que le backend (resolveCurrentPriceCents) :
  // le PRIX COURANT (table prices = ce que la caisse facture) prime TOUJOURS.
  // Le prix figé du lot (unit_price_ttc = prix saisi à la réception) n'est qu'un
  // fallback pour un produit fraîchement reçu sans prix de vente encore posé —
  // jamais un ancien prix d'historique. (Avant : le lot primait → l'étiquette
  // annonçait un prix périmé alors que le backend imprimait le prix courant.)
  const resolvedPrice = useCallback((): { cents: number | null; source: string } => {
    if (selectedProduct?.prices && selectedProduct.prices.length > 0
        && selectedProduct.prices[0].amount_cents != null) {
      return { cents: selectedProduct.prices[0].amount_cents, source: 'Prix courant' };
    }
    const selectedLot = lots.find(l => l.id === selectedLotId);
    if (selectedLot?.unit_price_ttc_cents) {
      return { cents: selectedLot.unit_price_ttc_cents, source: `Lot ${selectedLot.lot_number} (à défaut de prix courant)` };
    }
    if (selectedProduct?.suggested_price_cents) {
      return { cents: selectedProduct.suggested_price_cents, source: 'Prix suggéré' };
    }
    return { cents: null, source: '' };
  }, [selectedLotId, lots, selectedProduct]);

  const formatCents = (cents: number): string => {
    return `${(cents / 100).toFixed(2)} EUR`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const loadPreview = async () => {
    if (!selectedProduct?.id || !selectedTemplateId) return;

    try {
      setPreviewing(true);
      const result = await labelTemplatesApi.preview({
        productId: selectedProduct.id,
        lotId: selectedLotId || undefined,
        templateId: selectedTemplateId,
      });
      setPreviewUrl(result.preview_url);
    } catch (error: any) {
      console.error('Erreur preview:', error);
      setPreviewUrl(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedProduct?.id || !selectedTemplateId) {
      setStatusMessage({ type: 'error', text: 'Veuillez sélectionner un produit et un template' });
      return;
    }

    try {
      setPrinting(true);
      setStatusMessage({ type: 'info', text: 'Impression en cours...' });

      const bridgeStatus = await getHardwareBridgeStatus();
      const useLocalBridge = bridgeStatus?.hardware.zebra.available ?? false;
      const absente = zebraAbsenteIci(bridgeStatus);
      if (absente) throw new Error(absente);

      if (useLocalBridge) {
        console.log('[PrintShelfLabels] Utilisation Hardware Bridge local');

        const previewResult = await labelTemplatesApi.preview({
          productId: selectedProduct.id,
          lotId: selectedLotId || undefined,
          templateId: selectedTemplateId,
        });

        const zpl = (previewResult as any).zpl;
        if (!zpl) {
          throw new Error('ZPL non disponible dans la réponse');
        }

        for (let i = 0; i < quantity; i++) {
          const result = await hardwareBridgeZebra.printZPL(zpl);
          if (!result.success) {
            throw new Error(result.message);
          }
        }

        setStatusMessage({ type: 'success', text: `${quantity} étiquette(s) imprimée(s) via Hardware Bridge` });
        setTimeout(onClose, 1500);
      } else {
        console.log('[PrintShelfLabels] Utilisation API backend');
        const result = await labelTemplatesApi.print({
          productId: selectedProduct.id,
          lotId: selectedLotId || undefined,
          templateId: selectedTemplateId,
          quantity,
        });

        if (result.success) {
          setStatusMessage({ type: 'success', text: result.message });
          setTimeout(onClose, 1500);
        } else {
          setStatusMessage({ type: 'error', text: result.message });
        }
      }
    } catch (error: any) {
      console.error('Erreur impression:', error);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Erreur lors de l\'impression',
      });
    } finally {
      setPrinting(false);
    }
  };

  const price = resolvedPrice();

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Imprimer Étiquettes</h2>
              <p className="mt-1 text-sm text-gray-500">
                Sélectionnez le produit et le template pour imprimer
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>

          {/* Formulaire */}
          <div className="space-y-4">
            {/* Product Search */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Produit *
              </label>
              {selectedProduct ? (
                <div className="flex items-center justify-between px-3 py-2 border border-green-300 rounded-md bg-green-50">
                  <div>
                    <span className="font-medium text-gray-900">{selectedProduct.name}</span>
                    {selectedProduct.code && (
                      <span className="ml-2 text-sm text-gray-500">({selectedProduct.code})</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={clearProduct}
                    className="text-gray-400 hover:text-gray-600 text-lg"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onFocus={() => productResults.length > 0 && setShowProductDropdown(true)}
                    placeholder="Rechercher un produit (nom, code, code-barres)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoComplete="off"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Tapez au moins 2 caractères pour rechercher
                  </p>
                </>
              )}

              {/* Dropdown des résultats */}
              {showProductDropdown && productResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {productResults.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => selectProduct(product)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-xs text-gray-500">
                        {product.code && `Code: ${product.code}`}
                        {product.code && product.barcode && ' \u2022 '}
                        {product.barcode && `CB: ${product.barcode}`}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Lot Dropdown */}
            {selectedProduct && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot
                </label>
                {lotsLoading ? (
                  <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-500">
                    Chargement des lots...
                  </div>
                ) : lots.length === 0 ? (
                  <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-500">
                    Aucun lot actif — le prix produit sera utilisé
                  </div>
                ) : (
                  <select
                    value={selectedLotId}
                    onChange={(e) => setSelectedLotId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Aucun lot (prix produit)</option>
                    {lots.map((lot) => (
                      <option key={lot.id} value={lot.id}>
                        {lot.lot_number}
                        {lot.supplier_name ? ` — ${lot.supplier_name}` : ''}
                        {lot.unit_price_ttc_cents ? ` — ${formatCents(lot.unit_price_ttc_cents)}` : ''}
                        {lot.expiry_date ? ` — DLC ${formatDate(lot.expiry_date)}` : ''}
                        {` (${lot.stock_display + lot.stock_backroom} en stock)`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Prix résolu */}
            {selectedProduct && (
              <div className={`px-3 py-2 rounded-md text-sm ${
                price.cents
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <span className="font-medium">Prix sur l'étiquette : </span>
                {price.cents ? (
                  <>
                    <span className="font-bold text-lg">{formatCents(price.cents)}</span>
                    <span className="ml-2 text-gray-500">({price.source})</span>
                  </>
                ) : (
                  <span className="text-yellow-700">Aucun prix trouvé</span>
                )}
              </div>
            )}

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template *
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Sélectionner un template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.width_mm}×{template.height_mm}mm)
                    {template.is_default ? ' [Défaut]' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <span className="min-w-[3rem] text-center text-2xl font-semibold text-gray-900">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(100, q + 1))}
                  disabled={quantity >= 100}
                  className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* Prévisualisation auto */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Prévisualisation
                </label>
                {selectedProduct?.id && selectedTemplateId && (
                  <button
                    type="button"
                    onClick={loadPreview}
                    disabled={previewing}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    Rafraîchir
                  </button>
                )}
              </div>
              <div className="border border-gray-300 rounded-md p-4 bg-gray-50 min-h-[100px] flex items-center justify-center">
                {previewing ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                    Chargement...
                  </div>
                ) : previewUrl ? (
                  <img src={previewUrl} alt="Prévisualisation" className="max-w-full h-auto" />
                ) : (
                  <p className="text-sm text-gray-400">
                    {selectedProduct?.id && selectedTemplateId
                      ? 'Chargement de la prévisualisation...'
                      : 'Sélectionnez un produit et un template'}
                  </p>
                )}
              </div>
            </div>

            {/* Status Message */}
            {statusMessage && (
              <div className={`px-4 py-3 rounded-md text-sm ${
                statusMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : statusMessage.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}>
                {statusMessage.text}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={printing}
            >
              Annuler
            </button>
            <button
              onClick={handlePrint}
              disabled={printing || !selectedProduct?.id || !selectedTemplateId}
              className="flex-1 px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {printing ? 'Impression...' : `Imprimer${quantity > 1 ? ` (${quantity})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
